import { TrimIcon } from '@components/Icons/TrimIcon';
import { MomentThumbnail } from '@components/MomentThumbnail/MomentThumbnail';
import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { formatDurationToString } from '@lib/utils/units';
import { Tooltip } from '@mui/material';
import { TimestampOverlay } from '@pages/Library/components/MediaCard/components/TimestampOverlay';
import { useMomentMergeStore } from '@stores/MomentMerge/MomentMergeStore';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MomentMenuButton } from './MomentMenuButton';
import css from './MomentMergeTimeline.module.scss';

type MomentMergeTimelineProps = {
  onTrimExpand?: (momentId: string) => void;
  activeMomentId?: string | null;
  onMomentDoubleClick?: (momentId: string) => void;
  onMomentReordered?: (momentId: string) => void;
  onMenuOpen?: () => void;
};

export const MomentMergeTimeline = observer(
  ({
    onTrimExpand,
    activeMomentId,
    onMomentDoubleClick,
    onMomentReordered,
    onMenuOpen,
  }: MomentMergeTimelineProps) => {
    const momentMergeStore = useMomentMergeStore();
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollableRef = useRef<HTMLDivElement>(null);
    const menuAnchorElRef = useRef<HTMLElement | null>(null);

    // Update ref when menuAnchorEl changes
    useEffect(() => {
      menuAnchorElRef.current = menuAnchorEl;
    }, [menuAnchorEl]);

    const getModificationTooltipText = useCallback((moment: any) => {
      if (!moment.modifications.trimmed) {
        return 'Modified';
      }

      const originalDuration =
        moment.modifications.trimmed.originalTimestamps.endTime -
        moment.modifications.trimmed.originalTimestamps.startTime;
      const currentDuration = moment.duration;
      const rawDifference = currentDuration - originalDuration;
      const formattedDifference = formatDurationToString(Math.abs(rawDifference), {
        precision: 0,
        detail: 2,
      });
      const difference = Math.abs(rawDifference) < 1 ? '< 1 sec' : formattedDifference;

      if (rawDifference > 0) {
        return `Expanded by ${difference}`;
      } else if (rawDifference < 0) {
        return `Trimmed by ${difference}`;
      } else {
        return 'No change';
      }
    }, []);

    // Create preview order while dragging
    const previewClips = useMemo(() => {
      if (
        !isDragging ||
        draggedIndex === null ||
        dragOverIndex === null ||
        draggedIndex === dragOverIndex
      ) {
        return momentMergeStore.clips;
      }

      const clips = [...momentMergeStore.clips];
      const [movedClip] = clips.splice(draggedIndex, 1);
      clips.splice(dragOverIndex, 0, movedClip);
      return clips;
    }, [momentMergeStore.clips, isDragging, draggedIndex, dragOverIndex]);

    const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
      setDraggedIndex(index);
      setIsDragging(true);

      // Store the original container position and scroll when drag starts
      if (scrollableRef.current) {
        const rect = scrollableRef.current.getBoundingClientRect();
      }

      // Create a custom drag image that includes overlays
      const draggedElement = e.currentTarget as HTMLElement;
      const dragImage = draggedElement.cloneNode(true) as HTMLElement;

      // Ensure the cloned element has the same styles
      dragImage.style.position = 'absolute';
      dragImage.style.top = '-9999px';
      dragImage.style.left = '-9999px';
      dragImage.style.zIndex = '-9999';
      dragImage.style.pointerEvents = 'none';

      // Add to DOM temporarily
      document.body.appendChild(dragImage);

      // Set as drag image
      e.dataTransfer.setDragImage(dragImage, 80, 45); // Center of the 160x90 element

      // Clean up after drag starts
      setTimeout(() => {
        if (document.body.contains(dragImage)) {
          document.body.removeChild(dragImage);
        }
      }, 0);

      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index.toString());
    }, []);

    const calculateTargetIndex = useCallback(
      (clientX: number) => {
        if (!scrollableRef.current) return 0;

        const itemWidth = 160; // Width of each moment item
        const gap = 16; // Gap between items
        const totalItemWidth = itemWidth + gap;

        // Get the current container position and scroll
        const currentRect = scrollableRef.current.getBoundingClientRect();
        const currentScrollLeft = scrollableRef.current.scrollLeft;

        // Calculate cursor position relative to the current container position
        const cursorX = clientX - currentRect.left + currentScrollLeft;

        // Handle cases where cursor is outside the container horizontally
        if (cursorX < 0) {
          return 0;
        }

        if (cursorX > scrollableRef.current.scrollWidth) {
          return momentMergeStore.clips.length;
        }

        // Calculate which item the cursor is over and its position within that item
        const itemIndex = Math.floor(cursorX / totalItemWidth);
        const positionWithinItem = cursorX - itemIndex * totalItemWidth;

        // If cursor is in the gap area (after the item), insert after this item
        if (positionWithinItem > itemWidth) {
          return itemIndex + 1;
        }

        // If cursor is within the item, always insert at that position
        // This will push the current item and all items after it to the right
        return itemIndex;
      },
      [momentMergeStore.clips.length],
    );

    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (draggedIndex === null) {
          return;
        }

        const targetIndex = calculateTargetIndex(e.clientX);
        setDragOverIndex(targetIndex);
      },
      [draggedIndex, calculateTargetIndex],
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      // Only clear if we're leaving the container entirely
      if (!scrollableRef.current?.contains(e.relatedTarget as Node)) {
        setDragOverIndex(null);
      }
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
          // Get the moment ID before reordering (the one being moved)
          const movedMomentId = momentMergeStore.clips[draggedIndex].id;

          // Perform the reorder
          momentMergeStore.reorderClips(draggedIndex, dragOverIndex);

          // Call the callback with the moved moment's ID
          onMomentReordered?.(movedMomentId);
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
        setIsDragging(false);
      },
      [draggedIndex, dragOverIndex, momentMergeStore, onMomentReordered],
    );

    const handleDragEnd = useCallback(() => {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setIsDragging(false);
    }, []);

    // Global mouse event handlers for dragging outside container
    const handleGlobalMouseMove = useCallback(
      (e: globalThis.MouseEvent) => {
        if (!isDragging || draggedIndex === null) {
          return;
        }

        const targetIndex = calculateTargetIndex(e.clientX);
        setDragOverIndex(targetIndex);
      },
      [isDragging, draggedIndex, calculateTargetIndex],
    );

    const handleGlobalMouseUp = useCallback(
      (e: globalThis.MouseEvent) => {
        if (!isDragging) {
          return;
        }

        // Check if mouse is over the container
        if (scrollableRef.current && scrollableRef.current.contains(e.target as Node)) {
          // Mouse is over container, let the drop event handle it
          return;
        }

        // Mouse is outside container, cancel the drag
        setDraggedIndex(null);
        setDragOverIndex(null);
        setIsDragging(false);
      },
      [isDragging],
    );

    // Add global event listeners when dragging
    useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleGlobalMouseMove, { passive: true });
        document.addEventListener('mouseup', handleGlobalMouseUp);
        // Prevent default drag behavior to avoid conflicts
        document.addEventListener('dragover', (e) => e.preventDefault(), { passive: false });

        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
          document.removeEventListener('mouseup', handleGlobalMouseUp);
          document.removeEventListener('dragover', (e) => e.preventDefault());
        };
      }
    }, [isDragging, handleGlobalMouseMove, handleGlobalMouseUp]);

    // Auto-scroll to center the active moment when video is playing
    useEffect(() => {
      if (!activeMomentId || !scrollableRef.current || isDragging || !momentMergeStore.isPlaying)
        return;

      // Close any open menu when auto-scrolling
      if (menuAnchorElRef.current) {
        setMenuAnchorEl(null);
      }

      // Find the index of the active moment
      const activeIndex = momentMergeStore.clips.findIndex((clip) => clip.id === activeMomentId);
      if (activeIndex === -1) return;

      // Calculate the position to scroll to center the active moment
      const itemWidth = 160; // Width of each moment item
      const gap = 16; // Gap between items
      const totalItemWidth = itemWidth + gap;

      // Calculate the center position of the active moment
      const activeMomentCenter = activeIndex * totalItemWidth + itemWidth / 2;

      // Calculate the container center
      const containerWidth = scrollableRef.current.clientWidth;
      const containerCenter = containerWidth / 2;

      // Calculate the scroll position to center the active moment
      const targetScrollLeft = activeMomentCenter - containerCenter;

      // Ensure scroll position is within bounds
      const maxScrollLeft = scrollableRef.current.scrollWidth - containerWidth;
      const clampedScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

      // Smooth scroll to the target position
      scrollableRef.current.scrollTo({
        left: clampedScrollLeft,
        behavior: 'smooth',
      });
    }, [activeMomentId, momentMergeStore.clips, isDragging, momentMergeStore.isPlaying]);

    return (
      <GradientScrollDiv
        ref={scrollableRef}
        className={classNames({
          [css.momentList]: true,
          [css.dragging]: isDragging,
        })}
        innerDivClassName={classNames(css.momentList, {
          [css.dragging]: isDragging,
        })}
        horizontalScrolling={true}
      >
        <div
          ref={containerRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={css.momentListContainer}
        >
          {previewClips.map((moment, index) => {
            // Find the original index of this moment to determine if it's being dragged
            const originalIndex = momentMergeStore.clips.findIndex((clip) => clip.id === moment.id);
            const isDragged = draggedIndex === originalIndex;

            return (
              <div
                className={classNames(css.momentItem, {
                  [css.dragging]: isDragged,
                  [css.active]: activeMomentId === moment.id,
                })}
                key={moment.id}
                draggable
                onDragStart={(e) => handleDragStart(e, originalIndex)}
                onDragEnd={handleDragEnd}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  onMomentDoubleClick?.(moment.id);
                }}
              >
                <span
                  className={css.momentItemTitle}
                  title={`${moment.extendedMediaId.libraryId}:${moment.extendedMediaId.mediaId}:${moment.beginEndTimestamp.startTime}-${moment.beginEndTimestamp.endTime}`}
                >
                  {/* TODO: Pass isAudio to MomentThumbnail for audio-only media once merge modal supports audio */}
                  <MomentThumbnail
                    libraryId={moment.extendedMediaId.libraryId}
                    mediaId={moment.extendedMediaId.mediaId}
                    timestamp={moment.beginEndTimestamp.startTime}
                  />
                </span>
                <div className={css.overlays}>
                  <div className={css.gradientOverlay} />
                  {Object.keys(moment.modifications).length > 0 && (
                    <Tooltip title={getModificationTooltipText(moment)} placement="top" arrow>
                      <div className={css.lowerRightCircle}>
                        <TrimIcon />
                      </div>
                    </Tooltip>
                  )}
                  <div className={css.timestampOverlay}>
                    <TimestampOverlay duration={moment.duration} show={true} />
                  </div>
                  <div className={css.menuButton}>
                    <MomentMenuButton
                      momentId={moment.id}
                      onMenuOpen={onMenuOpen}
                      onDuplicate={() => {
                        const momentIndex = momentMergeStore.clips.findIndex(
                          (clip) => clip.id === moment.id,
                        );
                        if (momentIndex !== -1) {
                          momentMergeStore.duplicateClip(momentIndex);
                        }
                      }}
                      onTrimExpand={() => onTrimExpand?.(moment.id)}
                      onRemove={() => {
                        const momentIndex = momentMergeStore.clips.findIndex(
                          (clip) => clip.id === moment.id,
                        );
                        if (momentIndex !== -1) {
                          momentMergeStore.removeClip(momentIndex);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </GradientScrollDiv>
    );
  },
);
